// Use type-only imports so the @mediapipe runtime code stays out of the
// initial bundle. The actual module is loaded via a dynamic import in
// `useEffect` below — that puts MediaPipe in its own Rollup chunk
// (`vendor-mediapipe`) which is only fetched in the background AFTER the
// app paints. Saves ~9 MB on initial JS execution / parse / eval.
import type {
  FaceLandmarker as FaceLandmarkerType,
} from '@mediapipe/tasks-vision';
import React, { createContext, useContext, useEffect, useState } from 'react';

export const MODEL_URL = '/mediapipe/models/face_landmarker.task';
export const WASM_URL = '/mediapipe/wasm';

interface FaceLandmarkerContextType {
  faceLandmarker: FaceLandmarkerType | null;
  loading: boolean;
  error: Error | null;
  /** Internal marker: `true` when the consumer is reading the default
   *  ctx value (i.e. no <FaceLandmarkerProvider> above in the tree). */
  _noProvider?: boolean;
}

const FaceLandmarkerContext = createContext<FaceLandmarkerContextType>({
  faceLandmarker: null,
  loading: true,
  error: null,
  _noProvider: true,
});

async function createFaceLandmarker(): Promise<FaceLandmarkerType> {
  // Dynamic import keeps MediaPipe out of the initial vendor bundle —
  // Rollup will emit a separate `vendor-mediapipe-*.js` chunk that
  // only the browser fetches when this code path actually runs.
  const { FaceLandmarker, FilesetResolver } = await import(
    '@mediapipe/tasks-vision'
  );
  const resolver = await FilesetResolver.forVisionTasks(WASM_URL);
  return FaceLandmarker.createFromOptions(resolver, {
    baseOptions: { modelAssetPath: MODEL_URL, delegate: 'GPU' },
    runningMode: 'VIDEO',
    numFaces: 1,
  });
}

export const FaceLandmarkerProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [faceLandmarker, setFaceLandmarker] =
    useState<FaceLandmarkerType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let cancelled = false;
    // Defer the preload by one tick so React commits the initial paint
    // before we start the (~9 MB) MediaPipe chunk download. Without
    // this, the dynamic import fires during the first JS task and
    // ends up competing with hydration for CPU.
    const startTimer = setTimeout(() => {
      (async () => {
        try {
          setLoading(true);
          const instance = await createFaceLandmarker();
          if (!cancelled) {
            setFaceLandmarker(instance);
            // eslint-disable-next-line no-console
            console.log('FaceLandmarker preloaded successfully');
          }
        } catch (err) {
          if (!cancelled) {
            console.error('Failed to preload FaceLandmarker', err);
            setError(err as Error);
          }
        } finally {
          if (!cancelled) setLoading(false);
        }
      })();
    }, 0);

    return () => {
      cancelled = true;
      clearTimeout(startTimer);
    };
  }, []);

  return (
    <FaceLandmarkerContext.Provider value={{ faceLandmarker, loading, error }}>
      {children}
    </FaceLandmarkerContext.Provider>
  );
};

export function usePreloadFaceLandmarker(): FaceLandmarkerContextType {
  const ctx = useContext(FaceLandmarkerContext);
  // Fallback state — only populated/used when there's no provider above.
  const [fallback, setFallback] = useState<FaceLandmarkerContextType>({
    faceLandmarker: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    if (!ctx._noProvider) return; // a real provider is above us — do nothing
    let cancelled = false;
    (async () => {
      try {
        const instance = await createFaceLandmarker();
        if (!cancelled) {
          setFallback({ faceLandmarker: instance, loading: false, error: null });
        }
      } catch (err) {
        if (!cancelled) {
          setFallback({
            faceLandmarker: null,
            loading: false,
            error: err as Error,
          });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ctx._noProvider]);

  return ctx._noProvider ? fallback : ctx;
}
